import { t } from '@lingui/macro'
import { useCallback, useState } from 'react'
import { CandidateContactLogCreate } from '../client/models/CandidateContactLogCreate'
import { CandidateContactLogPatch } from '../client/models/CandidateContactLogPatch'
import { CarePartnerContactLogCreate } from '../client/models/CarePartnerContactLogCreate'
import { CarePartnerContactLogPatch } from '../client/models/CarePartnerContactLogPatch'
import { ContactLogResponse } from '../client/models/ContactLogResponse'
import { PatientContactLogCreate } from '../client/models/PatientContactLogCreate'
import { PatientContactLogPatch } from '../client/models/PatientContactLogPatch'
import { AspenContactLogsService } from '../client/services/AspenContactLogsService'

export type EntityType = 'patient' | 'candidate' | 'care-partner'

export interface LogAttemptData {
    contactType: ContactLogResponse['contactType']
    outcome: ContactLogResponse['attemptOutcome']
    notes: string
    nextContactTime?: Date | null
}

export interface UpdateAttemptData extends Partial<LogAttemptData> {
    contactAttemptId: string
}

interface UseContactAttemptsProps {
    entityType: EntityType
    entityId: string
}

interface UseContactAttemptsReturn {
    contactAttempts: ContactLogResponse[]
    isLoading: boolean
    error: string | null
    fetchContactAttempts: () => Promise<void>
    submitAttempt: (data: LogAttemptData) => Promise<void>
    updateAttempt: (data: UpdateAttemptData) => Promise<boolean>
}

export const useContactAttempts = ({ entityType, entityId }: UseContactAttemptsProps): UseContactAttemptsReturn => {
    const [contactAttempts, setContactAttempts] = useState<ContactLogResponse[]>([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const fetchContactAttempts = useCallback(async () => {
        if (!entityType || !entityId) return

        setIsLoading(true)
        setError(null)
        try {
            if (entityType === 'patient') {
                const response = await AspenContactLogsService.listPatientContactAttemptsPatientPatientIdContactLogsGet({
                    patientId: entityId
                })
                setContactAttempts(response)
            } else if (entityType === 'care-partner') {
                const response = await AspenContactLogsService.listCarePartnerContactAttemptsCarePartnerCarePartnerIdContactLogsGet({
                    carePartnerId: entityId
                })
                setContactAttempts(response)
            } else {
                const response = await AspenContactLogsService.listCandidateContactAttemptsCandidateCandidateIdContactLogsGet({
                    candidateId: entityId
                })
                setContactAttempts(response)
            }
        } catch (err) {
            setError(t`Failed to fetch contact attempts. Please try again.`)
            console.error(t`Failed to fetch contact attempts:`, err)
        } finally {
            setIsLoading(false)
        }
    }, [entityType, entityId])

    const submitAttempt = useCallback(
        async (data: LogAttemptData) => {
            if (!entityType || !entityId) return

            setIsLoading(true)
            setError(null)

            try {
                const requestBody: PatientContactLogCreate | CandidateContactLogCreate | CarePartnerContactLogCreate = {
                    contactType: data.contactType,
                    attemptOutcome: data.outcome,
                    notes: data.notes || null,
                    suggestedNextContactTime: data.nextContactTime ? data.nextContactTime.toISOString() : null
                }

                if (entityType === 'patient') {
                    const response = await AspenContactLogsService.createPatientContactAttemptPatientPatientIdContactLogsPost({
                        patientId: entityId,
                        requestBody: requestBody as PatientContactLogCreate
                    })
                    setContactAttempts((prevAttempts) => [response, ...prevAttempts])
                } else if (entityType === 'care-partner') {
                    const response = await AspenContactLogsService.createCarePartnerContactAttemptCarePartnerCarePartnerIdContactLogsPost({
                        carePartnerId: entityId,
                        requestBody: requestBody as CarePartnerContactLogCreate
                    })
                    setContactAttempts((prevAttempts) => [response, ...prevAttempts])
                } else {
                    const response = await AspenContactLogsService.createCandidateContactAttemptCandidateCandidateIdContactLogsPost({
                        candidateId: entityId,
                        requestBody: requestBody as CandidateContactLogCreate
                    })
                    setContactAttempts((prevAttempts) => [response, ...prevAttempts])
                }
            } catch (err) {
                setError(t`Failed to save contact attempt. Please try again.`)
                console.error(t`Failed to save contact attempt:`, err)
            } finally {
                setIsLoading(false)
            }
        },
        [entityType, entityId]
    )

    const updateAttempt = useCallback(
        async (data: UpdateAttemptData): Promise<boolean> => {
            if (!entityType || !entityId) return false

            setIsLoading(true)
            setError(null)

            try {
                // Build request body with only provided fields (undefined = don't send, null = clear)
                const requestBody = Object.fromEntries(
                    Object.entries({
                        contactType: data.contactType,
                        attemptOutcome: data.outcome,
                        notes: data.notes,
                        suggestedNextContactTime: data.nextContactTime === undefined ? undefined : data.nextContactTime?.toISOString() ?? null
                    }).filter(([, value]) => value !== undefined)
                ) as PatientContactLogPatch | CandidateContactLogPatch | CarePartnerContactLogPatch

                let updatedAttempt: ContactLogResponse

                if (entityType === 'patient') {
                    updatedAttempt = await AspenContactLogsService.updatePatientContactAttemptPatientPatientIdContactLogsContactAttemptIdPatch({
                        patientId: entityId,
                        contactAttemptId: data.contactAttemptId,
                        requestBody: requestBody as PatientContactLogPatch
                    })
                } else if (entityType === 'care-partner') {
                    updatedAttempt =
                        await AspenContactLogsService.updateCarePartnerContactAttemptCarePartnerCarePartnerIdContactLogsContactAttemptIdPatch({
                            carePartnerId: entityId,
                            contactAttemptId: data.contactAttemptId,
                            requestBody: requestBody as CarePartnerContactLogPatch
                        })
                } else {
                    updatedAttempt = await AspenContactLogsService.updateCandidateContactAttemptCandidateCandidateIdContactLogsContactAttemptIdPatch({
                        candidateId: entityId,
                        contactAttemptId: data.contactAttemptId,
                        requestBody: requestBody as CandidateContactLogPatch
                    })
                }

                // Update the attempt in the local state
                setContactAttempts((prevAttempts) => prevAttempts.map((attempt) => (attempt.id === data.contactAttemptId ? updatedAttempt : attempt)))

                return true
            } catch (err: unknown) {
                // Handle 403 error (24-hour edit window exceeded)
                if (err && typeof err === 'object' && 'status' in err && err.status === 403) {
                    setError(t`Contact attempt can only be edited within 24 hours of creation.`)
                } else {
                    setError(t`Failed to update contact attempt. Please try again.`)
                }
                console.error(t`Failed to update contact attempt:`, err)
                return false
            } finally {
                setIsLoading(false)
            }
        },
        [entityType, entityId]
    )

    return {
        contactAttempts,
        isLoading,
        error,
        fetchContactAttempts,
        submitAttempt,
        updateAttempt
    }
}
